import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';

export function formatClientLabel(slug) {
  return slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function RagClientSelect({
  clients,
  value,
  onChange,
  loading,
  disabled,
  allowAll = true,
  placeholder = 'Tous les clients',
  label = 'Client',
  getOptionValue = (client) => client.slug,
  getOptionLabel = (client) => formatClientLabel(client.slug),
  allowCreate = false,
  onCreate = null,
  creating = false,
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef(null);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b), 'fr')),
    [clients, getOptionLabel],
  );

  const filteredClients = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return sortedClients;
    }
    return sortedClients.filter((client) => {
      const optionLabel = getOptionLabel(client).toLowerCase();
      const optionValue = String(getOptionValue(client)).toLowerCase();
      return optionLabel.includes(query) || optionValue.includes(query);
    });
  }, [filter, getOptionLabel, getOptionValue, sortedClients]);

  const selectedClient = clients.find((client) => getOptionValue(client) === value);
  const selectedLabel = selectedClient
    ? getOptionLabel(selectedClient)
    : placeholder;

  const createName = filter.trim();
  const hasExactMatch = createName !== '' && filteredClients.some(
    (client) => getOptionLabel(client).localeCompare(createName, 'fr', { sensitivity: 'accent' }) === 0,
  );
  const showCreate = Boolean(
    allowCreate
    && typeof onCreate === 'function'
    && createName.length >= 2
    && !hasExactMatch
    && !creating,
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreate = async () => {
    if (!showCreate) return;
    try {
      await onCreate(createName);
      setOpen(false);
      setFilter('');
    } catch {
      // L’appelant gère l’erreur (toast) ; on laisse le dropdown ouvert.
    }
  };

  return (
    <div className="rag-client-select" ref={containerRef}>
      {label ? <label className="form-label text-sm mb-4">{label}</label> : null}
      <div className="position-relative">
        <button
          type="button"
          className="form-control text-start d-flex align-items-center justify-content-between"
          onClick={() => !disabled && setOpen((prev) => !prev)}
          disabled={disabled || loading || creating}
        >
          <span className="text-truncate">
            {loading || creating ? (creating ? 'Création du dossier…' : 'Chargement…') : selectedLabel}
          </span>
          <Icon icon="mdi:chevron-down" className="text-secondary-light flex-shrink-0" />
        </button>

        {open && !loading && !creating && (
          <div className="rag-client-select__dropdown card shadow-sm">
            <div className="p-8 border-bottom">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Rechercher un client…"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                autoFocus
              />
            </div>
            <ul className="rag-client-select__list list-unstyled mb-0">
              {allowAll && (
                <li>
                  <button
                    type="button"
                    className={`rag-client-select__option w-100 text-start ${value === '' ? 'active' : ''}`}
                    onClick={() => {
                      onChange('');
                      setOpen(false);
                      setFilter('');
                    }}
                  >
                    {placeholder}
                  </button>
                </li>
              )}
              {filteredClients.map((client) => {
                const optionValue = getOptionValue(client);
                return (
                  <li key={optionValue}>
                    <button
                      type="button"
                      className={`rag-client-select__option w-100 text-start ${value === optionValue ? 'active' : ''}`}
                      onClick={() => {
                        onChange(optionValue);
                        setOpen(false);
                        setFilter('');
                      }}
                    >
                      <span className="fw-medium">{getOptionLabel(client)}</span>
                    </button>
                  </li>
                );
              })}
              {filteredClients.length === 0 && !showCreate && (
                <li className="px-12 py-8 text-secondary-light text-sm">Aucun client trouvé</li>
              )}
              {showCreate && (
                <li className="border-top">
                  <button
                    type="button"
                    className="rag-client-select__option rag-client-select__option--create w-100 text-start"
                    onClick={() => { void handleCreate(); }}
                  >
                    <Icon icon="mdi:folder-plus-outline" className="me-1" width={18} />
                    <span>
                      Créer le dossier <strong>« {createName} »</strong> sur Drive
                    </span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
